import { useEffect, useRef, useState } from "react";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiFieldSearch,
  EuiSpacer,
  EuiText,
  EuiIcon,
  EuiButtonIcon,
  EuiBadge,
  EuiPanel,
  EuiLoadingSpinner,
  EuiToolTip,
  EuiCallOut,
  EuiFieldText,
  EuiTextArea,
  EuiFormRow,
  EuiSwitch,
  EuiHorizontalRule,
  EuiButtonEmpty,
  EuiButton,
} from "@elastic/eui";
import { ComplexQuestionEditorProps, OpsanioLocationValue } from "../../../";
import { useAnketa } from "../../context_provider";
import { useAnketaI18n } from "src/internationalization";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { NOMINATIM_ENDPOINT } from "src/helper";
import {
  TILE_ATTRIBUTION,
  TILE_LAYER_URL,
  hasCoordinates,
  useMapTiles,
} from "src/helper/map";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const NOMINATIM_BASE_URL = NOMINATIM_ENDPOINT;

const MIN_QUERY_LENGTH = 3;

/** Free-text address fields for the manual fallback (see addManualLocation). */
interface ManualAddress {
  street: string;
  postalCode: string;
  city: string;
  country: string;
  lat: string;
  lng: string;
}

const EMPTY_MANUAL: ManualAddress = {
  street: "",
  postalCode: "",
  city: "",
  country: "",
  lat: "",
  lng: "",
};

/** "Street, 12345 City, Country" - the same shape Nominatim returns. */
const manualDisplayName = (address: ManualAddress): string =>
  [
    address.street.trim(),
    [address.postalCode.trim(), address.city.trim()].filter(Boolean).join(" "),
    address.country.trim(),
  ]
    .filter(Boolean)
    .join(", ");

const DEBOUNCE_MS = 300;

interface ComplexLocationEditor extends ComplexQuestionEditorProps {
  type?: "single" | "multiple";
}

export const ComplexQuestionLocationEditor = ({
  question,
  setValue,
  invalid,
  type = "single",
}: ComplexLocationEditor): JSX.Element => {
  const ctx = useAnketa();
  const { translate } = useAnketaI18n();

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [searchValue, setSearchValue] = useState<string>("");
  const [searchResults, setSearchResults] = useState<OpsanioLocationValue[]>(
    [],
  );
  const [selectedLocations, setSelectedLocations] = useState<
    OpsanioLocationValue[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [manual, setManual] = useState<ManualAddress>({ ...EMPTY_MANUAL });

  const tiles = useMapTiles();
  // A refused tile server (403) renders an empty grey grid - hide the map
  // entirely rather than showing a broken one.
  const showMap = tiles.status === "available";

  useEffect(() => {
    // The container only exists once the tile probe came back positive.
    if (!showMap || !mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [51.505, 10.09],
      zoom: 5,
      zoomControl: true,
    });

    L.tileLayer(TILE_LAYER_URL, {
      attribution: TILE_ATTRIBUTION,
      maxZoom: 19,
    }).addTo(map);

    mapInstanceRef.current = map;
    setMapReady(true);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      setMapReady(false);
    };
  }, [showMap]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    // Manually entered sites without coordinates simply get no pin.
    const pinnable = selectedLocations.filter(hasCoordinates);
    if (pinnable.length === 0) return;

    const bounds: L.LatLngTuple[] = [];

    pinnable.forEach((loc) => {
      const marker = L.marker([loc.lat, loc.lng], {
        icon: L.divIcon({
          className: "",
          html: `<div style="
            background:#006BB4;
            width:14px;height:14px;
            border-radius:50%;
            border:2px solid white;
            box-shadow:0 1px 4px rgba(0,0,0,0.4)
          "></div>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
      })
        .addTo(map)
        .bindPopup(`<strong>${loc.displayName}</strong>`);

      markersRef.current.push(marker);
      bounds.push([loc.lat, loc.lng]);
    });

    if (bounds.length === 1) {
      map.setView(bounds[0], 13);
    } else {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [selectedLocations, mapReady]);

  useEffect(() => {
    const raw = question.fact.getStrValue(ctx.i18nContext);
    if (!raw) {
      setIsLoaded(true);
      return;
    }
    try {
      const parsed: OpsanioLocationValue[] = JSON.parse(raw);
      if (Array.isArray(parsed)) setSelectedLocations(parsed);
    } catch {
      // not JSON, ignore
    }
    setIsLoaded(true);
  }, []);

  const searchLocations = async (query: string) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsSearching(true);
    setSearchError(null);

    try {
      const url = new URL(`${NOMINATIM_BASE_URL}/search`);
      url.searchParams.set("q", query);
      url.searchParams.set("format", "json");
      url.searchParams.set("addressdetails", "1");
      url.searchParams.set("limit", "5");

      const res = await fetch(url.toString(), {
        signal: controller.signal,
        headers: { "Accept-Language": ctx.i18nContext.language || "en" },
      });

      if (!res.ok) {
        setSearchError(
          translate("anketa.questionnaire.editor.location.search.failed", {
            values: { status: res.status },
            defaultMessage:
              "Search failed ({{status}}). Is the Nominatim container running?",
          }),
        );
        setShowResults(false);
        setManualOpen(true);
        return;
      }

      const data = await res.json();

      const results: OpsanioLocationValue[] = data.map((item: any) => ({
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        displayName: item.display_name,
        city: item.address?.city || item.address?.town || item.address?.village,
        country: item.address?.country,
        postalCode: item.address?.postcode,
        street: item.address?.road,
      }));

      setSearchResults(results);
      setShowResults(results.length > 0);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      setSearchError(
        translate("anketa.questionnaire.editor.location.search.unreachable", {
          defaultMessage:
            "Could not reach the geocoder. Make sure the Docker stack is running: docker compose -f docker-compose.osm.yml up -d",
        }),
      );
      setManualOpen(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchValue(val);
    setSearchError(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.trim().length < MIN_QUERY_LENGTH) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    debounceRef.current = setTimeout(
      () => searchLocations(val.trim()),
      DEBOUNCE_MS,
    );
  };

  const selectLocation = (loc: OpsanioLocationValue) => {
    let updated: OpsanioLocationValue[];
    if (type === "single") {
      updated = [loc];
    } else {
      const isDuplicate = selectedLocations.some((l) =>
        hasCoordinates(l) && hasCoordinates(loc)
          ? l.lat === loc.lat && l.lng === loc.lng
          : l.displayName === loc.displayName,
      );
      if (isDuplicate) return;
      updated = [...selectedLocations, loc];
    }
    setSelectedLocations(updated);
    setValue(JSON.stringify(updated));
    setSearchValue("");
    setSearchResults([]);
    setShowResults(false);
    setSearchError(null);
  };

  // Fallback for an unreachable/blocked geocoder: take the address straight
  // from the user. Coordinates are optional - without them the site is stored
  // and rendered normally, it just gets no map pin (and NetBox site sync, which
  // requires coordinates, will report the site as incomplete).
  const addManualLocation = () => {
    const displayName = manualDisplayName(manual);
    if (!manual.city.trim() && !manual.street.trim()) {
      setManualError(
        translate("anketa.questionnaire.editor.location.manual.incomplete", {
          defaultMessage: "Enter at least a street or a city.",
        }),
      );
      return;
    }

    const rawLat = manual.lat.trim().replace(",", ".");
    const rawLng = manual.lng.trim().replace(",", ".");
    let lat: number | undefined;
    let lng: number | undefined;

    if (rawLat !== "" || rawLng !== "") {
      const parsedLat = Number(rawLat);
      const parsedLng = Number(rawLng);
      if (
        rawLat === "" ||
        rawLng === "" ||
        !hasCoordinates({ lat: parsedLat, lng: parsedLng })
      ) {
        setManualError(
          translate("anketa.questionnaire.editor.location.manual.badCoords", {
            defaultMessage:
              "Enter latitude and longitude together as decimal degrees (-90..90 / -180..180), or leave both empty.",
          }),
        );
        return;
      }
      lat = parsedLat;
      lng = parsedLng;
    }

    setManualError(null);
    selectLocation({
      lat,
      lng,
      displayName,
      street: manual.street.trim() || undefined,
      postalCode: manual.postalCode.trim() || undefined,
      city: manual.city.trim() || undefined,
      country: manual.country.trim() || undefined,
    });
    setManual({ ...EMPTY_MANUAL });
    setManualOpen(false);
  };

  const updateManual = (field: keyof ManualAddress, value: string) => {
    setManual((prev) => ({ ...prev, [field]: value }));
    setManualError(null);
  };

  const removeLocation = (index: number) => {
    const updated = selectedLocations.filter((_, i) => i !== index);
    setSelectedLocations(updated);
    setValue(JSON.stringify(updated));
  };

  // Update a per-site text field (comment / weitereInfos / breakouts).
  const updateLocationField = (
    index: number,
    field: "comment" | "weitereInfos" | "breakouts",
    value: string,
  ) => {
    const updated = selectedLocations.map((loc, i) =>
      i === index ? { ...loc, [field]: value } : loc,
    );
    setSelectedLocations(updated);
    setValue(JSON.stringify(updated));
  };

  // "Hauptstandort" is single-select: marking one site primary clears the rest
  // so the fact parser always resolves a single primary location.
  const setPrimary = (index: number, isPrimary: boolean) => {
    const updated = selectedLocations.map((loc, i) => ({
      ...loc,
      isPrimary: isPrimary ? i === index : i === index ? false : loc.isPrimary,
    }));
    setSelectedLocations(updated);
    setValue(JSON.stringify(updated));
  };

  const openGoogleMaps = (loc: OpsanioLocationValue) => {
    window.open(
      `https://www.google.com/maps?q=${loc.lat},${loc.lng}`,
      "_blank",
    );
  };

  const openAppleMaps = (loc: OpsanioLocationValue) => {
    const label = encodeURIComponent(loc.city || loc.displayName.split(",")[0]);
    window.open(
      `https://maps.apple.com/?ll=${loc.lat},${loc.lng}&q=${label}`,
      "_blank",
    );
  };

  return (
    <EuiFlexItem grow style={{ minWidth: "200px" }}>
      <div style={{ position: "relative" }}>
        <EuiFieldSearch
          placeholder={translate(
            "anketa.questionnaire.editor.location.search.placeholder",
            {
              values: { min: MIN_QUERY_LENGTH },
              defaultMessage:
                "Search for a location (min. {{min}} characters)...",
            },
          )}
          value={searchValue}
          onChange={handleSearchChange}
          isLoading={isSearching}
          isInvalid={invalid}
          disabled={question.readonly}
          fullWidth
          isClearable
        />

        {showResults && searchResults.length > 0 && (
          <EuiPanel
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1000,
              maxHeight: "240px",
              overflowY: "auto",
              padding: 0,
            }}
          >
            {searchResults.map((result, i) => (
              <div
                key={i}
                onClick={() => selectLocation(result)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--aq-border)",
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLDivElement).style.background =
                    "var(--aq-hover)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLDivElement).style.background = "")
                }
              >
                <EuiFlexGroup
                  gutterSize="s"
                  alignItems="center"
                  responsive={false}
                >
                  <EuiFlexItem grow={false}>
                    <EuiIcon type="mapMarker" size="m" color="primary" />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiText size="s" style={{ fontWeight: 500 }}>
                      {result.city ||
                        result.street ||
                        result.displayName.split(",")[0]}
                    </EuiText>
                    <EuiText size="xs" color="subdued">
                      {result.displayName}
                    </EuiText>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </div>
            ))}
          </EuiPanel>
        )}
      </div>

      {searchError && (
        <>
          <EuiSpacer size="xs" />
          <EuiCallOut
            title={searchError}
            color="warning"
            iconType="alert"
            size="s"
          />
        </>
      )}

      {!question.readonly && (
        <>
          <EuiSpacer size="xs" />
          <EuiFlexGroup gutterSize="none" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                size="xs"
                iconType={manualOpen ? "arrowUp" : "pencil"}
                onClick={() => setManualOpen(!manualOpen)}
              >
                {translate(
                  "anketa.questionnaire.editor.location.manual.toggle",
                  { defaultMessage: "Enter address manually" },
                )}
              </EuiButtonEmpty>
            </EuiFlexItem>
          </EuiFlexGroup>
        </>
      )}

      {manualOpen && !question.readonly && (
        <>
          <EuiSpacer size="xs" />
          <EuiPanel paddingSize="s" hasBorder>
            <EuiFormRow
              label={translate(
                "anketa.questionnaire.editor.location.manual.street",
                { defaultMessage: "Street and number" },
              )}
              fullWidth
            >
              <EuiFieldText
                value={manual.street}
                onChange={(e) => updateManual("street", e.target.value)}
                fullWidth
              />
            </EuiFormRow>
            <EuiFlexGroup gutterSize="s">
              <EuiFlexItem grow={false} style={{ minWidth: "120px" }}>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.location.manual.postalCode",
                    { defaultMessage: "Postal code" },
                  )}
                >
                  <EuiFieldText
                    value={manual.postalCode}
                    onChange={(e) => updateManual("postalCode", e.target.value)}
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.location.manual.city",
                    { defaultMessage: "City" },
                  )}
                  fullWidth
                >
                  <EuiFieldText
                    value={manual.city}
                    onChange={(e) => updateManual("city", e.target.value)}
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.location.manual.country",
                    { defaultMessage: "Country" },
                  )}
                  fullWidth
                >
                  <EuiFieldText
                    value={manual.country}
                    onChange={(e) => updateManual("country", e.target.value)}
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiFlexGroup gutterSize="s">
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.location.manual.lat",
                    { defaultMessage: "Latitude (optional)" },
                  )}
                  fullWidth
                >
                  <EuiFieldText
                    value={manual.lat}
                    placeholder="51.505"
                    onChange={(e) => updateManual("lat", e.target.value)}
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.editor.location.manual.lng",
                    { defaultMessage: "Longitude (optional)" },
                  )}
                  fullWidth
                >
                  <EuiFieldText
                    value={manual.lng}
                    placeholder="10.09"
                    onChange={(e) => updateManual("lng", e.target.value)}
                    fullWidth
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiText size="xs" color="subdued">
              {translate("anketa.questionnaire.editor.location.manual.hint", {
                defaultMessage:
                  "Without coordinates the site is saved but cannot be shown on a map.",
              })}
            </EuiText>

            {manualError && (
              <>
                <EuiSpacer size="xs" />
                <EuiCallOut
                  title={manualError}
                  color="danger"
                  iconType="alert"
                  size="s"
                />
              </>
            )}

            <EuiSpacer size="s" />
            <EuiFlexGroup gutterSize="s" justifyContent="flexEnd">
              <EuiFlexItem grow={false}>
                <EuiButton size="s" fill onClick={addManualLocation}>
                  {translate(
                    "anketa.questionnaire.editor.location.manual.add",
                    { defaultMessage: "Add address" },
                  )}
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPanel>
        </>
      )}

      <EuiSpacer size="s" />

      {selectedLocations.map((loc, i) => (
        <div key={i}>
          <EuiPanel paddingSize="s" hasBorder color={loc.isPrimary ? "primary" : "plain"}>
            <EuiFlexGroup
              gutterSize="s"
              alignItems="center"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <EuiBadge
                  color="hollow"
                  iconType="cross"
                  iconSide="right"
                  iconOnClick={
                    question.readonly ? undefined : () => removeLocation(i)
                  }
                  iconOnClickAriaLabel={translate(
                    "anketa.questionnaire.editor.location.remove.aria",
                    { defaultMessage: "Remove location" },
                  )}
                >
                  {loc.displayName.split(",")[0] || loc.city}
                </EuiBadge>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="xs" color="subdued">
                  {loc.displayName}
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem grow={false} hidden={!hasCoordinates(loc)}>
                <EuiToolTip
                  content={translate(
                    "anketa.questionnaire.editor.location.openGoogleMaps",
                    { defaultMessage: "Open in Google Maps" },
                  )}
                >
                  <EuiButtonIcon
                    aria-label={translate(
                      "anketa.questionnaire.editor.location.openGoogleMaps",
                      { defaultMessage: "Open in Google Maps" },
                    )}
                    iconType="globe"
                    size="xs"
                    color="primary"
                    onClick={() => openGoogleMaps(loc)}
                  />
                </EuiToolTip>
              </EuiFlexItem>
              <EuiFlexItem grow={false} hidden={!hasCoordinates(loc)}>
                <EuiToolTip
                  content={translate(
                    "anketa.questionnaire.editor.location.openAppleMaps",
                    { defaultMessage: "Open in Apple Maps" },
                  )}
                >
                  <EuiButtonIcon
                    aria-label={translate(
                      "anketa.questionnaire.editor.location.openAppleMaps",
                      { defaultMessage: "Open in Apple Maps" },
                    )}
                    iconType="visMapRegion"
                    size="xs"
                    color="text"
                    onClick={() => openAppleMaps(loc)}
                  />
                </EuiToolTip>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiHorizontalRule margin="s" />

            <EuiSwitch
              label={translate(
                "anketa.questionnaire.editor.location.primary.label",
                { defaultMessage: "Primary site" },
              )}
              checked={!!loc.isPrimary}
              disabled={question.readonly}
              onChange={(e) => setPrimary(i, e.target.checked)}
            />
            <EuiSpacer size="s" />
            <EuiFormRow
              label={translate(
                "anketa.questionnaire.editor.location.comment.label",
                { defaultMessage: "Comment" },
              )}
              fullWidth
            >
              <EuiFieldText
                value={loc.comment ?? ""}
                onChange={(e) =>
                  updateLocationField(i, "comment", e.target.value)
                }
                disabled={question.readonly}
                fullWidth
              />
            </EuiFormRow>
            <EuiFormRow
              label={translate(
                "anketa.questionnaire.editor.location.moreInfo.label",
                { defaultMessage: "Additional site information" },
              )}
              fullWidth
            >
              <EuiTextArea
                value={loc.weitereInfos ?? ""}
                onChange={(e) =>
                  updateLocationField(i, "weitereInfos", e.target.value)
                }
                disabled={question.readonly}
                fullWidth
                rows={2}
              />
            </EuiFormRow>
            <EuiFormRow
              label={translate(
                "anketa.questionnaire.editor.location.breakouts.label",
                { defaultMessage: "Breakouts in network" },
              )}
              fullWidth
            >
              <EuiFieldText
                value={loc.breakouts ?? ""}
                onChange={(e) =>
                  updateLocationField(i, "breakouts", e.target.value)
                }
                disabled={question.readonly}
                fullWidth
              />
            </EuiFormRow>
          </EuiPanel>
          <EuiSpacer size="s" />
        </div>
      ))}

      {showMap && (
        <div
          ref={mapRef}
          style={{
            height: "320px",
            width: "100%",
            borderRadius: "6px",
            overflow: "hidden",
            border: "1px solid var(--aq-border)",
            opacity: isLoaded ? 1 : 0.5,
            transition: "opacity 0.3s",
          }}
        />
      )}

      {tiles.status === "blocked" && (
        <EuiText size="xs" color="subdued">
          <EuiIcon type="mapMarker" size="s" />{" "}
          {translate("anketa.questionnaire.editor.location.map.unavailable", {
            values: { status: tiles.httpStatus ?? "" },
            defaultMessage:
              "Map view unavailable - the tile server refuses to deliver map tiles {{status}}. The addresses below are saved regardless.",
          })}
        </EuiText>
      )}

      {(!isLoaded || tiles.status === "checking") && (
        <EuiFlexGroup justifyContent="center" style={{ marginTop: "8px" }}>
          <EuiFlexItem grow={false}>
            <EuiLoadingSpinner size="m" />
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
    </EuiFlexItem>
  );
};
